import AppHeader from '../components/Header/AppHeader';
import OfflineBanner from '../components/OfflineBanner/OfflineBanner';
import { ScreenSizeContextProvider } from '../context/ScreenSizeContext';
import AuthContextProvider from '../context/AuthContext';
import Main from '../pages/Main';
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ToastProvider } from '@ordr-as/umami-react/toast';
import { OverlayProvider } from '@ordr-as/umami-react/modal';
import { LanguageProvider } from '../context/LanguageContext';
import ScrollToTop from '../components/ScrollToTop';
import ErrorBoundary from '../containers/ErrorBoundary';
import { PinScreenSettingsContextProvider } from '../context/PinScreenSettingsContext';
import { ApiFetcherProvider } from '../context/ApiFetcherProvider';
import { MenuBuilderFetcherProvider } from '../context/MenuBuilderFetcherProvider';
import { persistQueryClient } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { VersionNotifier } from '../components/VersionNotifier';
import { isProblemDetail } from '../utils/problemDetail';
import type { ProblemDetail } from '../utils/problemDetail';
import { useErrorHandler } from '../hooks/useErrorHandler';

let globalMutationErrorHandler: ((error: unknown) => void) | null = null;

function GlobalMutationErrorBridge() {
  const throwErrorToBoundary = useErrorHandler();
  globalMutationErrorHandler = throwErrorToBoundary;
  return null;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 5 * 60_000,
    },
  },
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (mutation.meta?.['skipGlobalErrorBoundary']) {
        return;
      }
      if (isProblemDetail(error) && (error as ProblemDetail).status !== 403 && globalMutationErrorHandler) {
        globalMutationErrorHandler(error);
      }
    },
  }),
});

const persister = createSyncStoragePersister({
  serialize: (client) => JSON.stringify(client),
  storage: window.localStorage,
});

persistQueryClient({
  dehydrateOptions: {
    shouldDehydrateQuery: (query) => !!query.options.meta?.['persist'],
  },
  maxAge: 1000 * 60 * 60 * 24,
  persister,
  queryClient,
});

const PrivateRoute = () => (
  <ScreenSizeContextProvider>
    <LanguageProvider>
      <ToastProvider>
        <VersionNotifier />
        <OverlayProvider>
          <AuthContextProvider>
            <QueryClientProvider client={queryClient}>
              <ApiFetcherProvider>
                <MenuBuilderFetcherProvider>
                  <PinScreenSettingsContextProvider>
                    <ScrollToTop />
                    <div className="h-screen flex flex-col overflow-hidden">
                      <OfflineBanner />
                      <AppHeader />
                      <div className="flex-1 overflow-auto">
                        <ErrorBoundary>
                          <GlobalMutationErrorBridge />
                          <Main />
                        </ErrorBoundary>
                      </div>
                    </div>
                  </PinScreenSettingsContextProvider>
                  <ReactQueryDevtools initialIsOpen={false} />
                </MenuBuilderFetcherProvider>
              </ApiFetcherProvider>
            </QueryClientProvider>
          </AuthContextProvider>
        </OverlayProvider>
      </ToastProvider>
    </LanguageProvider>
  </ScreenSizeContextProvider>
);
export default PrivateRoute;
